import React from 'react'
import { Link } from 'react-router-dom'

interface PaymentData {
  transactionId: string
  amount: number
  method: string
  status: 'success' | 'failed' | 'pending'
  timestamp: string
}

interface BookingData {
  roomName: string
  roomType: string
  checkIn: string
  checkOut: string
  guests: number
  confirmationNumber: string
  guestName: string
  guestEmail: string
}

interface BookingEmailStatus {
  sent: boolean
  recipient: string | null
  warning: string | null
}

interface PaymentConfirmationProps {
  paymentData: PaymentData
  bookingData: BookingData
  bookingEmailStatus?: BookingEmailStatus | null
}

const PaymentConfirmation: React.FC<PaymentConfirmationProps> = ({
  paymentData,
  bookingData,
  bookingEmailStatus
}) => {
  const formatCurrency = (amount: number) => {
    return `R${amount.toLocaleString()}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getPaymentMethodLabel = (method: string) => {
    const normalizedMethod = String(method || '').trim().toLowerCase()

    if (normalizedMethod === 'card' || normalizedMethod === 'yoco') {
      return 'Credit/Debit Card'
    }

    if (normalizedMethod === 'eft') {
      return 'EFT Transfer'
    }

    return method || 'Payment'
  }

  const calculateNights = () => {
    const checkIn = new Date(bookingData.checkIn)
    const checkOut = new Date(bookingData.checkOut)
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 3600 * 24))
  }

  return (
    <>
    {/* ── Print Receipt (only shown when printing) ── */}
    <div className="hidden print:block p-10 bg-white text-gray-900" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Letterhead */}
      <div style={{ borderBottom: '3px solid #2c5530', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#2c5530', margin: 0 }}>Marina Glen Holiday Resort</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#555', fontSize: '0.9rem' }}>bookings@marinaglen.co.za &nbsp;|&nbsp; marinaglen.co.za</p>
      </div>

      <h2 style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1a1a1a' }}>Payment Receipt</h2>

      {/* Confirmation banner */}
      <div style={{ background: paymentData.method === 'eft' ? '#fffbeb' : '#f0fdf4', border: paymentData.method === 'eft' ? '1px solid #fde68a' : '1px solid #bbf7d0', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ margin: 0, fontWeight: 'bold', color: paymentData.method === 'eft' ? '#92400e' : '#166534', fontSize: '1rem' }}>
          {paymentData.method === 'eft' ? '⏳ EFT Booking Received — Awaiting Payment' : '✔ Payment Successful — Booking Confirmed'}
        </p>
        <p style={{ margin: '0.25rem 0 0', color: paymentData.method === 'eft' ? '#92400e' : '#166534', fontSize: '0.9rem' }}>Confirmation Number: <strong>{bookingData.confirmationNumber}</strong></p>
        {paymentData.method === 'eft' && (
          <p style={{ margin: '0.5rem 0 0', color: '#78350f', fontSize: '0.85rem' }}>Please complete your EFT transfer using your confirmation number as the reference. Your booking will be confirmed once payment is received.</p>
        )}
      </div>

      {/* Two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }}>
        {/* Booking Details */}
        <div>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>Accommodation</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <tbody>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280', width: '45%' }}>Room</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600' }}>{bookingData.roomName}</td></tr>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280' }}>Check-in</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600' }}>{formatDate(bookingData.checkIn)}</td></tr>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280' }}>Check-out</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600' }}>{formatDate(bookingData.checkOut)}</td></tr>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280' }}>Duration</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600' }}>{calculateNights()} night{calculateNights() !== 1 ? 's' : ''}</td></tr>
              <tr><td style={{ color: '#6b7280' }}>Guests</td><td style={{ fontWeight: '600' }}>{bookingData.guests}</td></tr>
            </tbody>
          </table>
        </div>

        {/* Payment Details */}
        <div>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>Payment</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <tbody>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280', width: '45%' }}>Transaction ID</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{paymentData.transactionId}</td></tr>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280' }}>Method</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600' }}>{getPaymentMethodLabel(paymentData.method)}</td></tr>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280' }}>Date</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600' }}>{formatDateTime(paymentData.timestamp)}</td></tr>
              <tr><td style={{ paddingBottom: '0.4rem', color: '#6b7280' }}>Status</td><td style={{ paddingBottom: '0.4rem', fontWeight: '600', color: paymentData.method === 'eft' ? '#92400e' : '#166534' }}>{paymentData.method === 'eft' ? 'Pending — Awaiting EFT' : 'Paid'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Guest Details */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>Guest</h3>
        <p style={{ margin: '0 0 0.25rem', fontSize: '0.9rem' }}><span style={{ color: '#6b7280' }}>Name:</span> <strong>{bookingData.guestName}</strong></p>
        <p style={{ margin: 0, fontSize: '0.9rem' }}><span style={{ color: '#6b7280' }}>Email:</span> <strong>{bookingData.guestEmail}</strong></p>
      </div>

      {/* Total */}
      <div style={{ background: '#f8fafc', border: '2px solid #2c5530', borderRadius: '8px', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1a1a1a' }}>{paymentData.method === 'eft' ? 'Total Amount Due' : 'Total Amount Paid'}</span>
        <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c5530' }}>{formatCurrency(paymentData.amount)}</span>
      </div>

      {/* Footer */}
      <p style={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '1rem', margin: 0 }}>
        Printed {new Date().toLocaleDateString()} &nbsp;·&nbsp; Marina Glen Holiday Resort &nbsp;·&nbsp; Check-in 14:00 &nbsp;·&nbsp; Check-out 10:00
      </p>
    </div>

    {/* ── Normal screen view (hidden when printing) ── */}
    <div className="min-h-screen flex items-center justify-center print:hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-2xl w-full mx-4">
        {/* Success Header */}
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          padding: '2rem',
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)'
          }}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '1rem',
            textShadow: '3px 3px 6px rgba(0, 0, 0, 0.8)'
          }}>
            {paymentData.method === 'eft' ? 'Booking Received!' : 'Payment Successful!'}
          </h1>
          
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.9)',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
            marginBottom: '0.5rem'
          }}>
            {paymentData.method === 'eft' ? 'Your booking is pending payment confirmation' : 'Your booking has been confirmed'}
          </p>
          
          <p style={{
            fontSize: '1rem',
            color: '#10B981',
            fontWeight: '600',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
          }}>
            Confirmation Number: {bookingData.confirmationNumber}
          </p>
        </div>

        {/* Booking Details */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
          }}>
            Booking Summary
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '1rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Accommodation Details
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Room Type
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {bookingData.roomName}
                  </p>
                </div>
                
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Check-in Date
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {formatDate(bookingData.checkIn)}
                  </p>
                </div>
                
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Check-out Date
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {formatDate(bookingData.checkOut)}
                  </p>
                </div>
                
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Duration & Guests
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {calculateNights()} nights • {bookingData.guests} guest{bookingData.guests > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '1rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Payment Information
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Transaction ID
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0,
                    fontFamily: 'monospace'
                  }}>
                    {paymentData.transactionId}
                  </p>
                </div>
                
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Payment Method
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {getPaymentMethodLabel(paymentData.method)}
                  </p>
                </div>
                
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Payment Date
                  </p>
                  <p style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {formatDateTime(paymentData.timestamp)}
                  </p>
                </div>
                
                <div style={{
                  background: 'rgba(8, 145, 178, 0.2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid rgba(8, 145, 178, 0.3)'
                }}>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                    margin: '0 0 0.25rem 0'
                  }}>
                    {paymentData.method === 'eft' ? 'Total Amount Due' : 'Total Amount Paid'}
                  </p>
                  <p style={{
                    color: '#0891b2',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {formatCurrency(paymentData.amount)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Guest Information */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
          }}>
            What's Next?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 style={{
                color: bookingEmailStatus?.sent ? '#10B981' : '#f59e0b',
                fontSize: '1.1rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                {bookingEmailStatus?.sent ? '📧 Confirmation Email Sent' : '📧 Email Status'}
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                lineHeight: '1.5',
                margin: 0
              }}>
                {bookingEmailStatus?.sent ? (
                  <>
                    A detailed confirmation email has been sent to{' '}
                    <span style={{ fontWeight: '600', color: 'white' }}>
                      {bookingEmailStatus.recipient || bookingData.guestEmail}
                    </span>
                    . Please check your inbox and spam folder.
                  </>
                ) : bookingEmailStatus?.warning ? (
                  bookingEmailStatus.warning
                ) : (
                  <>
                    We are finalizing your booking email for{' '}
                    <span style={{ fontWeight: '600', color: 'white' }}>
                      {bookingData.guestEmail}
                    </span>
                    . If it does not arrive shortly, please contact support with your confirmation number.
                  </>
                )}
              </p>
            </div>
            
            <div>
              <h3 style={{
                color: '#f97316',
                fontSize: '1.1rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                📱 Important Reminders
              </h3>
              <ul style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                lineHeight: '1.5',
                paddingLeft: '1rem',
                margin: 0
              }}>
                <li>Check-in time: 2:00 PM</li>
                <li>Check-out time: 10:00 AM</li>
                <li>Bring valid ID for all guests</li>
                <li>Review our amenities and policies</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => window.print()}
            style={{
              padding: '1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: '500',
              backdropFilter: 'blur(8px)',
              cursor: 'pointer',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
              transition: 'all 0.3s ease'
            }}
            className="hover:bg-white hover:bg-opacity-20"
          >
            📄 Print Receipt
          </button>
          
          <Link
            to="/booking-system/guest-dashboard"
            style={{
              display: 'block',
              padding: '1rem',
              background: 'rgba(8, 145, 178, 0.8)',
              color: 'white',
              textDecoration: 'none',
              border: '1px solid rgba(8, 145, 178, 0.5)',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: '500',
              backdropFilter: 'blur(8px)',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            className="hover:bg-opacity-90"
          >
            👤 Guest Dashboard
          </Link>
          
          <Link
            to="/booking-system"
            style={{
              display: 'block',
              padding: '1rem',
              background: 'rgba(249, 115, 22, 0.8)',
              color: 'white',
              textDecoration: 'none',
              border: '1px solid rgba(249, 115, 22, 0.5)',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: '500',
              backdropFilter: 'blur(8px)',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            className="hover:bg-opacity-90"
          >
            🏠 Back to Home
          </Link>
        </div>

        {/* Contact Information */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            color: 'white',
            fontSize: '1.1rem',
            fontWeight: '600',
            marginBottom: '0.75rem',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
          }}>
            Need Help?
          </h3>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
            margin: '0 0 1rem 0'
          }}>
            Our team is here to assist you with any questions about your booking.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
            <a
              href="tel:+27123456789"
              style={{
                color: '#0891b2',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)'
              }}
            >
              📞 +27 12 345 6789
            </a>
            <a
              href="mailto:bookings@marinaglen.co.za"
              style={{
                color: '#0891b2',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)'
              }}
            >
              📧 bookings@marinaglen.co.za
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default PaymentConfirmation